import { DatePicker } from "@mui/x-date-pickers"
import { Box, Container, FormControlLabel, Grid, Switch, TextField, Tooltip, Typography } from "@mui/material"
import { useApiGet } from "auth/useApi2"
import { AppContext } from "components/AppContextProvider"
import BplTokenLink from "components/BplTokenLink"
import ClpTable from "components/ClpTable"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { apiEndpoints } from "generated/apiEndpoints"
import moment from "moment"
import { useContext, useEffect, useMemo, useState } from "react"
import { StyledTableContainer } from "shared/styled/Global"
import { useTranslation } from "react-i18next"

const Schedule = () => {
  const [details, showDetails] = useState(false)
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"))
  const [forwardAndReturn, showDriveForwardAndReturn] = useState(false)

  const [assignmentResult, getAssignment] = useApiGet()
  const loadingContext = useContext(GlobalLoaderContext)
  const appContext = useContext(AppContext)
  const { t } = useTranslation()
  
  useEffect(() => {
    getAssignment(apiEndpoints.planning.getassignment.replace("{date}", date))
  }, [getAssignment, date])

  useEffect(() => {
    loadingContext.setLoading(() => assignmentResult.status === 1)
  })

  const [assignments, setAssignments] = useState([])

  useEffect(() => {
    let assignmentList = []
    assignmentResult.data?.map((a) => {
      if (!forwardAndReturn) {
        a.uppdrag.filter((x) => x.ejFramAterkorning).map((pass) => assignmentList.push(pass))
      } else {
        a.uppdrag.map((pass) => assignmentList.push(pass))
      }
    })
    setAssignments(assignmentList)
  }, [assignmentResult.data, forwardAndReturn])

  const columns = useMemo(
    () => [
      {
        accessorFn: (row) => {
          return row.passStarttid ? moment(row.passStarttid).format("YYYY-MM-DD") : ""
        },
        header: t("global.date", "Date")
      },
      {
        accessorFn: (row) => {
          return row.starttid ? moment(row.starttid).format("HH:mm") : ""
        },
        header: t("global.start", "Start time")
      },
      {
        accessorKey: "turStarttidsträng",
        header: t("planning.departing_time_short", "Dep.time")
      },
      {
        accessorFn: (row) => {
          return row.sluttid ? moment(row.sluttid).format("HH:mm") : ""
        },
        header: t("global.end", "End")
      },
      {
        accessorKey: "beskrivning",
        header: t("planning.service", "Service")
      },
      {
        accessorKey: "fordonStr",
        header: t("global.vehicle", "Vehicle")
      },
      {
        accessorKey: "anmarkning",
        header: t("planning.note", "Note")
      }
    ],
    []
  )

  const columnsDetails = useMemo(
    () => [
      {
        accessorFn: (row) => {
          return row.passStarttid ? moment(row.passStarttid).format("YYYY-MM-DD") : ""
        },
        header: t("global.date", "Date")
      },
      {
        accessorFn: (row) => {
          return row.starttid ? moment(row.starttid).format("HH:mm") : ""
        },
        header: t("global.start", "Start")
      },
      {
        accessorFn: (row) => {
          return row.sluttid ? moment(row.sluttid).format("HH:mm") : ""
        },
        header: t("global.end", "End")
      },
      {
        accessorKey: "beskrivning",
        header: t("global.description", "Description")
      },
      {
        accessorKey: "fordonStr",
        header: t("global.vehicle", "Vehicle")
      },
      {
        accessorKey: "startplats",
        header: t("planning.starting_point", "Starting point")
      },
      {
        accessorKey: "slutplats",
        header: t("planning.ending_point", "Ending point")
      },
      {
        accessorKey: "anmarkning",
        header: t("global.note", "Note")
      }
    ],
    []
  )
  function disableDays(date) {
    return date > moment().add(appContext.appUser.customer.bplDaysShown, "days")
  }
  return (
    <div>
      <Container>
        <Box mb={1}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h1">{t("planning.my_assignments", "My assignments")}</Typography>
            {assignmentResult.status === 2 && (
              <BplTokenLink
                label={t("planning.show_assignments_BPL")}
                to={`client/utskrift/specifikation.aspx?personalid=${appContext.appUser.profile.bplId}&datum=${date}`}
                variant="contained"
                color="primary"
              />
            )}
          </Grid>
        </Box>
      </Container>
      <StyledTableContainer>
        <Box px={{ xs: 2, sm: 3 }} pt={{ xs: 2, sm: 3 }}>
          <Grid container direction="row" justifyContent="flex-end" alignItems="center" spacing={3}>
            <Grid item>
              <FormControlLabel
                label={t("planning.show_details", "Show details")}
                control={<Switch onChange={() => showDetails(!details)} id="details" checked={details} />}
              />
            </Grid>
            <Grid item>
              {!details ? (
                <Tooltip title={t("planning.explain_outbound_return_trips", "You can only view outbound/return trips if 'show details' is enabled")}>
                  <FormControlLabel
                    disabled={!details}
                    label={t("planning.show_outbound_return_trips", "Show outbound/return trips")}
                    control={
                      <Switch
                        onChange={() => showDriveForwardAndReturn(!forwardAndReturn)}
                        id="forwardAndReturn"
                        checked={forwardAndReturn}
                      />
                    }
                  />
                </Tooltip>
              ) : (
                <FormControlLabel
                  disabled={!details}
                  label={t("planning.show_outbound_return_trips", "Show outbound/return trips")}
                  control={
                    <Switch
                      onChange={() => showDriveForwardAndReturn(!forwardAndReturn)}
                      id="forwardAndReturn"
                      checked={forwardAndReturn}
                    />
                  }
                />
              )}
            </Grid>
            <Grid item>
              <DatePicker
                value={date ? moment(date) : null}
                onChange={(newValue) => setDate(moment(newValue).format("YYYY-MM-DD"))}
                shouldDisableDate={disableDays}
                renderInput={(params) => <TextField {...params} />}
              />
            </Grid>
          </Grid>
        </Box>

        {!details && (
          <ClpTable
            rows={assignmentResult.status === 2 && assignmentResult.data}
            loadingState={assignmentResult.status}
            columns={columns}
            print
          />
        )}
        {details && (
          <ClpTable
            rows={assignmentResult.status === 2 && assignments}
            loadingState={assignmentResult.status}
            columns={columnsDetails}
            print
          />
        )}
      </StyledTableContainer>
    </div>
  )
}

export default Schedule
